import { pageOffset, type PastDeadlineElection } from '@myrewards/contracts';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Link, RadioButton, RadioField, RadioGroup } from 'react-aria-components';
import { useSearchParams } from 'react-router';
import {
  chaseElection,
  closeElectionAsCash,
  extendElection,
  letterKeys,
  pastDeadlineKeys,
  pastDeadlineQuery,
} from '../../api/letters';
import { checkKeys } from '../../api/ops';
import { settingQuery } from '../../api/settings';
import { Hero, PageBody, TwoColumns } from '../../shell';
import {
  Button,
  ButtonLink,
  Card,
  clientPage,
  ConfirmDialog,
  cx,
  DataTable,
  EmptyState,
  FilterBar,
  formatCount,
  formatDate,
  formatMoney,
  formatShortDate,
  formatTimestamp,
  formatWeekdayDate,
  Icon,
  Pager,
  plural,
  scoped,
  StatusPill,
  TextField,
  useClampedPage,
  useListTable,
  usePaging,
  useToast,
  type DataColumn,
} from '../../ui';
import { nudgeText, reminderText } from '../../settings/model';
import { QUEUE_VIEW } from '../labels';
import { firstName, TEMPLATES_HREF } from '../letters/presenter';
import { SettingChange } from '../SettingChange';
import { NoMatches } from '../queue/NoMatches';
import { BackLink, Notice } from '../shared';
import {
  actionFromParam,
  chasedTodayWords,
  chaseEmail,
  closeSummary,
  contactWords,
  daysWords,
  electionProblemWords,
  extendProblem,
  firstAction,
  lateWords,
  nudgeWords,
  oldestLetter,
  PAST_DEADLINE_DEFAULT_SORT,
  PAST_DEADLINE_DEFAULT_WORDS,
  PAST_DEADLINE_SORTS,
  pastDeadlineFilters,
  pastDeadlineSentence,
  pastDeadlineTie,
  personLine,
  personSub,
  remindersText,
  selectedItem,
  type ElectionAction,
} from './presenter';
import o from '../Ops.module.css';
import s from '../Steps.module.css';
import { ManageTabs } from '../../manage/ManageTabs';

export const PAST_DEADLINE_HREF = QUEUE_VIEW.past_deadline.href;

/** The list's own address with one person's election open below it, at one action if given. */
export function pastDeadlineHref(lineId: string, action?: ElectionAction): string {
  const line = `${PAST_DEADLINE_HREF}?line=${encodeURIComponent(lineId)}`;
  return action === undefined ? line : `${line}&action=${action}`;
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const windowText = (v: unknown): string =>
  typeof v === 'number' ? `${plural(v, 'day', 'days')} from the award date` : 'Not set';

const SETTINGS = [
  { key: 'election.windowDays', label: 'Election window · PAA', words: windowText },
  {
    key: 'election.reminderDaysBefore',
    label: 'Reminders',
    words: (v: unknown) => capitalized(reminderText(v)),
  },
  { key: 'election.managerNudge', label: 'Manager nudge after the deadline', words: nudgeText },
] as const;

function SettingItem({ setting }: { setting: (typeof SETTINGS)[number] }): ReactElement {
  const view = useQuery(settingQuery(setting.key, 'PAA'));
  return (
    <div className={s.settingItem}>
      <dt className={cx('eyebrow', o.eyebrowMuted)}>{setting.label}</dt>
      <dd>
        {view.isError ? (
          <span className={o.muted}>This setting didn’t load. Reload the page to try again.</span>
        ) : view.data === undefined ? (
          <span className={o.muted}>Loading…</span>
        ) : (
          <>
            <span className={s.settingValue}>{setting.words(view.data.value)}</span>
            <span className={o.muted}>
              <SettingChange
                view={view.data}
                title={setting.label}
                subject="History · Elections and letters"
                words={setting.words}
              />
            </span>
          </>
        )}
      </dd>
    </div>
  );
}

/** The PAA election rules this list follows, each with who changed it last. */
function ElectionSettingsCard(): ReactElement {
  const canEdit = useQuery(settingQuery('election.windowDays', 'PAA')).data?.canEdit === true;
  return (
    <Card
      title="Election settings"
      compact
      help={{
        shows:
          'The PAA election window, when reminders go out, and whether the manager is nudged once a choice is late.',
        canDo: canEdit
          ? 'Change them in Settings › Elections and letters. A change applies to new letters; elections already open keep their dates.'
          : 'Read them here. The Stock Admin team changes them in Settings.',
      }}
      aside={
        canEdit ? (
          <Link href={TEMPLATES_HREF} className={o.inlineLink}>
            Change
          </Link>
        ) : undefined
      }
    >
      <dl className={s.settingList}>
        {SETTINGS.map((setting) => (
          <SettingItem key={setting.key} setting={setting} />
        ))}
      </dl>
    </Card>
  );
}

function WhatEachDoes(): ReactElement {
  return (
    <Card
      title="What each action does"
      helpAnchor="past-deadline-actions"
      tone="dark"
      compact
      titleSize="sm"
      help={{
        shows: 'What happens to the employee’s election after each action.',
        canDo: 'Pick the action under the person’s name. Every action is logged on the award.',
      }}
    >
      <dl className={s.onDarkList}>
        <div>
          <dt>Chase</dt>
          <dd>Emails the employee again, at most once a day. Stays here.</dd>
        </div>
        <div>
          <dt>Extend</dt>
          <dd>
            A new deadline and a reason. Back to Employee’s choice, with a new email and the usual
            reminders.
          </dd>
        </div>
        <div>
          <dt>Close as all cash</dt>
          <dd>Choice (a), with tax withheld. Goes to Payroll, who set the final tax.</dd>
        </div>
      </dl>
    </Card>
  );
}

const ACTIONS: readonly { id: ElectionAction; name: string; detail: string }[] = [
  { id: 'chase', name: 'Chase', detail: 'Email them again' },
  { id: 'extend', name: 'Extend', detail: 'Pick a new deadline' },
  { id: 'close', name: 'Close as all cash', detail: 'Choice (a), sent to Payroll' },
];

/** Chase: what the email says, an optional note, then send. Once a day per person. */
function ChaseForm({
  item,
  onDone,
}: {
  item: PastDeadlineElection;
  onDone: () => Promise<void>;
}): ReactElement {
  const toast = useToast();
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const first = firstName(item.person.name);
  const email = chaseEmail(item);
  const chased = chasedTodayWords(item);
  const tooLong = note.trim().length > 500;
  const chase = useMutation({
    mutationFn: () =>
      chaseElection(item.award.id, item.lineId, {
        version: item.version,
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      }),
    onSuccess: async () => {
      toast.show({
        tone: 'done',
        title: `Chase sent · ${item.person.name}`,
        body: `MyRewards is emailing ${first} now. It’s on the award’s history, and you can chase again tomorrow.`,
      });
      setNote('');
      setProblem(null);
      await onDone();
    },
    onError: async (e) => {
      setProblem(electionProblemWords(e, item.person.name));
      await onDone();
    },
  });

  if (chased !== null)
    return (
      <div className={o.stack}>
        <Notice tone="done">{chased}</Notice>
        <p className={o.muted}>
          {first} has the email. If nothing comes back, extend the deadline or close it as all cash.
        </p>
      </div>
    );
  if (!item.actions.chase)
    return (
      <Notice tone="info">
        Chasing isn’t available to you here. The Stock Admin team chases late elections.
      </Notice>
    );
  return (
    <form
      className={o.stack}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!tooLong && !chase.isPending) chase.mutate();
      }}
    >
      <section className={s.mailPreview} aria-label={`The email to ${item.person.name}`}>
        <dl className={s.mailHead}>
          <div>
            <dt>To</dt>
            <dd>{email.to}</dd>
          </div>
          <div>
            <dt>Subject</dt>
            <dd>{email.subject}</dd>
          </div>
        </dl>
        <p className={s.mailBody}>{email.body}</p>
        <p className={o.muted}>
          It links to the calculator in MyRewards and says the Stock Admin team can help.
        </p>
      </section>
      <TextField
        label="Add a note (optional)"
        multiline
        value={note}
        onChange={setNote}
        placeholder="For example: we can talk it through on Thursday"
        description="It goes in the email and on the award’s history."
        isInvalid={tooLong}
        {...(tooLong && { errorMessage: 'Keep the note to 500 characters.' })}
      />
      {/* Said here, not by React Aria's pending announcement: once the chase is sent this button
          goes, and that announcement would keep pointing at it (as ConfirmDialog does). */}
      <p className="visually-hidden" role="status" aria-live="polite">
        {chase.isPending ? 'Sending the email.' : ''}
      </p>
      <div className={s.buttonLine}>
        <Button
          type={chase.isPending ? 'button' : 'submit'}
          variant="navy"
          aria-disabled={chase.isPending}
          aria-busy={chase.isPending}
          data-pending={chase.isPending || undefined}
        >
          Send the email
        </Button>
        <span className={o.muted}>Stays here · logged on the award · once a day</span>
      </div>
      {problem !== null && <Notice tone="problem">{problem}</Notice>}
    </form>
  );
}

/** One person's late election: what's up for the choice, then chase, extend or close it. */
function PersonElection({
  item,
  asOf,
  requested,
  onAction,
  onDone,
}: {
  item: PastDeadlineElection;
  asOf: string;
  /** The action a row's button asked for (`?action=`), if any. */
  requested: ElectionAction | null;
  /** The choice here, for the page's address, so the row's buttons and the panel agree. */
  onAction: (action: ElectionAction) => void;
  onDone: () => Promise<void>;
}): ReactElement {
  const toast = useToast();
  // Where the panel opens: the row's choice, else Chase when it can go today, else Extend. It
  // stays put after a chase (the panel then says who chased), until another choice is made.
  const [action, setAction] = useState<ElectionAction>(requested ?? firstAction(item));
  // A row's button for this person while it's open: follow it (set during render, not after).
  const [followed, setFollowed] = useState(requested);
  if (requested !== followed) {
    setFollowed(requested);
    if (requested !== null) setAction(requested);
  }
  const [deadline, setDeadline] = useState('');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const first = firstName(item.person.name);
  const invalid = extendProblem(deadline, reason, asOf);
  const shown = tried ? invalid : null;

  const extend = useMutation({
    mutationFn: () =>
      extendElection(item.award.id, item.lineId, {
        version: item.version,
        deadlineDate: deadline,
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      toast.show({
        tone: 'done',
        title: `Deadline extended · ${item.person.name}`,
        body: `${first} has until ${formatDate(deadline)}, with an email and the usual reminders. It’s back at Employee’s choice.`,
      });
      setDeadline('');
      setReason('');
      setTried(false);
      setProblem(null);
      await onDone();
    },
    onError: async (e) => {
      setProblem(electionProblemWords(e, item.person.name));
      await onDone();
    },
  });

  const close = useMutation({
    mutationFn: (why: string) =>
      closeElectionAsCash(item.award.id, item.lineId, { version: item.version, reason: why }),
  });
  const summary = closeSummary(item);

  return (
    <div className={o.stack}>
      <div className={o.tiles}>
        <div className={cx(o.tile, o.tileCyan)}>
          <p className={cx('eyebrow', o.tileLabel)}>{scoped(first, 'cash portion')}</p>
          <p className={cx('disp', o.tileValue)}>{formatMoney(item.cashCents)}</p>
          <p className={o.tileNote}>Up for election</p>
        </div>
        {item.options > 0 && (
          <div className={o.tile}>
            <p className={cx('eyebrow', o.tileLabel)}>{scoped(first, 'options')}</p>
            <p className={cx('disp', o.tileValue)}>{formatCount(item.options)}</p>
            <p className={o.tileNote}>Not affected by the election</p>
          </div>
        )}
        <div className={cx(o.tile, o.tileProblem)}>
          <p className={cx('eyebrow', o.tileLabel)}>{scoped(first, 'election choice')}</p>
          <p className={cx('disp', o.tileValue)}>None yet</p>
          <p className={o.tileNote}>Choices (a) to (d)</p>
        </div>
      </div>
      <RadioGroup
        aria-label={`What to do about ${first}’s election`}
        value={action}
        onChange={(v) => {
          const next = v === 'close' ? 'close' : v === 'chase' ? 'chase' : 'extend';
          setAction(next);
          onAction(next);
          setProblem(null);
        }}
        orientation="horizontal"
        className={s.actionGroup}
      >
        {ACTIONS.map((a) => (
          <RadioField key={a.id} value={a.id}>
            <RadioButton className={s.holderCard}>
              <span className={s.radioDot} aria-hidden="true" />
              <span className={s.holderText}>
                <span className={s.holderName}>{a.name}</span>
                <span className={s.holderDetail}>
                  {a.id === 'chase' && item.chasedToday ? 'Chased today' : a.detail}
                </span>
              </span>
            </RadioButton>
          </RadioField>
        ))}
      </RadioGroup>
      {action === 'chase' ? (
        <ChaseForm item={item} onDone={onDone} />
      ) : action === 'extend' ? (
        <form
          className={o.stack}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            setTried(true);
            if (invalid === null && !extend.isPending) extend.mutate();
          }}
        >
          <div className={s.deadlineRow}>
            <TextField
              label="New deadline"
              type="date"
              value={deadline}
              onChange={setDeadline}
              isRequired
              isInvalid={shown?.field === 'deadline'}
              {...(shown?.field === 'deadline' && { errorMessage: shown.message })}
            />
            <p className={o.muted}>
              {first} gets an email with the new date, and the usual reminders run again.
            </p>
          </div>
          <TextField
            label="Reason"
            multiline
            value={reason}
            onChange={setReason}
            isRequired
            placeholder="For example: back from travel on the 15th, asked for a few more days"
            isInvalid={shown?.field === 'reason'}
            {...(shown?.field === 'reason' && { errorMessage: shown.message })}
          />
          {/* The person leaves this list once extended, and this button with them. */}
          <p className="visually-hidden" role="status" aria-live="polite">
            {extend.isPending ? 'Extending the deadline.' : ''}
          </p>
          <div className={s.buttonLine}>
            <Button
              type={extend.isPending ? 'button' : 'submit'}
              variant="navy"
              aria-disabled={extend.isPending}
              aria-busy={extend.isPending}
              data-pending={extend.isPending || undefined}
              isDisabled={!item.actions.extend}
            >
              Extend the deadline
            </Button>
            <span className={o.muted}>Back to Employee’s choice · logged with your reason</span>
          </div>
        </form>
      ) : (
        <div className={o.stack}>
          <p className={o.cardText}>
            {first} gets the whole {formatMoney(item.cashCents)} as cash, with tax withheld, the
            same as choice (a).
            {item.estimateACents !== null
              ? ` By the estimate at the check, that’s about ${formatMoney(Math.max(0, item.cashCents - item.estimateACents))} after tax. Payroll sets the final tax.`
              : ' Payroll sets the final tax.'}
            {item.options > 0 &&
              ` The ${plural(item.options, 'option', 'options')} aren’t affected.`}
          </p>
          <div className={s.buttonLine}>
            <Button
              variant="navy"
              isDisabled={!item.actions.closeAsCash}
              onPress={() => {
                setCloseError(null);
                setConfirming(true);
              }}
            >
              Close as all cash
            </Button>
            <span className={o.muted}>Goes to Payroll · logged with your reason</span>
          </div>
        </div>
      )}
      {problem !== null && <Notice tone="problem">{problem}</Notice>}
      <ConfirmDialog
        isOpen={confirming}
        onOpenChange={setConfirming}
        title={`Close ${first}’s election as all cash?`}
        intro={summary.intro}
        summary={summary.rows}
        reasonLabel="Why close it as cash?"
        confirmLabel="Close as all cash"
        error={closeError}
        onConfirm={async (why) => {
          try {
            await close.mutateAsync(why);
          } catch (e) {
            setCloseError(electionProblemWords(e, item.person.name));
            await onDone();
            return false;
          }
          toast.show({
            tone: 'done',
            title: `Closed as all cash · ${item.person.name}`,
            body: 'It’s with Payroll, who set the final tax. The decision is logged on the award.',
          });
          await onDone();
          return true;
        }}
      />
    </div>
  );
}

/** Every reminder, chase, extension and manager nudge for the person open below, oldest first. */
function ElectionHistory({ item }: { item: PastDeadlineElection }): ReactElement {
  const first = firstName(item.person.name);
  return (
    <Card
      title="Election history"
      help={{
        shows: `Every reminder, chase, extension and manager nudge for ${first}’s election, oldest first.`,
        canDo: 'Read who did what and when. The award record has the full history.',
      }}
    >
      {item.contacts.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="calendar"
          size="sm"
          headline="Nothing sent since the letter"
          body={`Reminders, chases, extensions and manager nudges for ${first} show here as they happen.`}
        />
      ) : (
        <ol className={s.contactList} aria-label={`${first}’s election history`}>
          {item.contacts.map((c, i) => {
            const words = contactWords(c);
            return (
              <li key={`${c.at}-${String(i)}`} className={s.contactItem}>
                <span className={s.contactWhen}>{formatTimestamp(c.at)}</span>
                <span className={s.contactWhat}>{words.title}</span>
                {words.note !== null && <span className={o.muted}>“{words.note}”</span>}
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}

/**
 * The person open below the list: their late election, and the actions for it. A row's Chase,
 * Extend or Close as all cash opens it at that action and moves focus to the person's name, so a
 * keyboard or screen-reader user lands on what they chose (SA4 board).
 */
function ElectionPanel({
  item,
  asOf,
  requested,
  onAction,
  focusRequest,
}: {
  item: PastDeadlineElection;
  asOf: string;
  requested: ElectionAction | null;
  onAction: (action: ElectionAction) => void;
  /** Counts the row buttons pressed: each one moves focus here. */
  focusRequest: number;
}): ReactElement {
  const queryClient = useQueryClient();
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focusRequest > 0) heading.current?.focus();
  }, [focusRequest]);
  const onDone = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: pastDeadlineKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['queue'] }),
      queryClient.invalidateQueries({ queryKey: ['home'] }),
      queryClient.invalidateQueries({ queryKey: letterKeys.one(item.award.id) }),
      queryClient.invalidateQueries({ queryKey: checkKeys.one(item.award.id) }),
    ]);
  };
  return (
    <div className={o.stack}>
      <Card
        title={
          item.person.eeNumber === null
            ? item.person.name
            : `${item.person.name} · EE# ${item.person.eeNumber}`
        }
        help={panelHelp}
        accent="cyan"
        headingRef={heading}
        badge={
          item.daysSinceLetter === null ? undefined : (
            <StatusPill
              size="sm"
              tone="problem"
              label={`${daysWords(item.daysSinceLetter)} since the letter`}
            />
          )
        }
      >
        <p className={cx('eyebrow', o.eyebrowMuted)}>
          {item.award.displayId} · Professional Achievement Award
        </p>
        <p className={o.muted}>
          {personLine(item)} · {lateWords(item.daysLate)}
        </p>
        <PersonElection
          key={item.lineId}
          item={item}
          asOf={asOf}
          requested={requested}
          onAction={onAction}
          onDone={onDone}
        />
        <Link href={`/awards/${item.award.id}`} className={o.inlineLink}>
          Award record and history
        </Link>
      </Card>
      <ElectionHistory item={item} />
    </div>
  );
}

const panelHelp = {
  shows:
    'One person’s late election: the cash up for the choice, the options it doesn’t touch, and who the person is.',
  canDo:
    'Email them again, give a new deadline with a reason, or close the election as all cash after a confirmation. Every action is logged on the award.',
};

/**
 * A row's actions, as on the board: Chase, Extend and Close as all cash, each opening the person
 * below at that action, where the email, the new date or the summary comes first. Nothing is done
 * from the row itself. Only the actions the person can take today; Open when there are none
 * (read-only, or in Preview).
 */
function RowActions({
  item,
  selected,
  onOpen,
}: {
  item: PastDeadlineElection;
  selected: boolean;
  onOpen: () => void;
}): ReactElement {
  const name = item.person.name;
  const links = [
    item.actions.chase && {
      action: 'chase' as const,
      label: 'Chase',
      aria: `Chase ${name}`,
      variant: 'ghost' as const,
    },
    item.actions.extend && {
      action: 'extend' as const,
      label: 'Extend',
      aria: `Extend the deadline for ${name}`,
      variant: 'ghost' as const,
    },
    item.actions.closeAsCash && {
      action: 'close' as const,
      label: 'Close as all cash',
      aria: `Close as all cash for ${name}`,
      variant: 'navy' as const,
    },
  ].filter((l) => l !== false);
  return (
    <div className={s.rowActions}>
      {selected && (
        <span className={s.openNow}>
          <Icon name="arrowRight" size={14} />
          Open below
        </span>
      )}
      {links.length === 0 ? (
        !selected && (
          <ButtonLink
            href={pastDeadlineHref(item.lineId)}
            size="sm"
            variant="ghost"
            aria-label={`Open ${name}’s election, ${item.award.displayId}`}
            onPress={onOpen}
          >
            Open
          </ButtonLink>
        )
      ) : (
        <div className={s.rowButtons}>
          {links.map((l) => (
            <ButtonLink
              key={l.action}
              href={pastDeadlineHref(item.lineId, l.action)}
              size="sm"
              variant={l.variant}
              aria-label={l.aria}
              onPress={onOpen}
            >
              {l.label}
            </ButtonLink>
          ))}
        </div>
      )}
    </div>
  );
}

function columns(
  selectedId: string | null,
  onOpen: () => void,
): DataColumn<PastDeadlineElection>[] {
  return [
    {
      id: 'employee',
      header: 'Employee',
      card: 'title',
      cell: (item) => (
        <>
          <span className={o.strong}>{item.person.name}</span>
          <span className={o.sub}>{personSub(item)}</span>
        </>
      ),
      sort: PAST_DEADLINE_SORTS.employee,
    },
    {
      id: 'award',
      header: 'Award',
      cell: (item) => (
        <>
          <Link href={`/awards/${item.award.id}`} className={o.strongLink}>
            {item.award.displayId}
          </Link>
          <span className={o.sub}>PAA · {formatMoney(item.cashCents)} cash portion</span>
        </>
      ),
      nowrap: true,
      sort: PAST_DEADLINE_SORTS.award,
    },
    {
      id: 'letter',
      header: 'Letter date',
      cell: (item) =>
        item.letterDate === null ? (
          <span className={o.muted}>Not released</span>
        ) : (
          formatShortDate(item.letterDate)
        ),
      nowrap: true,
      sort: PAST_DEADLINE_SORTS.letter,
    },
    {
      id: 'deadline',
      header: 'Deadline',
      cell: (item) => (
        <>
          {formatShortDate(item.deadline.date)}
          <span className={o.sub}>{lateWords(item.daysLate)}</span>
        </>
      ),
      nowrap: true,
      sort: PAST_DEADLINE_SORTS.deadline,
    },
    {
      id: 'days',
      header: 'Days since letter',
      cell: (item) =>
        item.daysSinceLetter === null ? (
          <span className={o.muted}>Not released</span>
        ) : (
          <span className={cx('disp', s.bigDays)}>{formatCount(item.daysSinceLetter)}</span>
        ),
      numeric: true,
      sort: PAST_DEADLINE_SORTS.days,
    },
    {
      id: 'reminders',
      header: 'Reminders',
      cell: (item) => {
        const r = remindersText(item);
        return (
          <>
            {r.count}
            {r.dates !== null && <span className={o.sub}>{r.dates}</span>}
          </>
        );
      },
      sort: PAST_DEADLINE_SORTS.reminders,
    },
    {
      id: 'nudge',
      header: 'Manager nudge',
      cell: (item) => <span className={o.sub}>{nudgeWords(item)}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item) =>
        item.chasedToday ? (
          <StatusPill size="sm" tone="waiting" label="Chased today" />
        ) : (
          <StatusPill size="sm" tone="problem" label="Past deadline" />
        ),
      nowrap: true,
      sort: PAST_DEADLINE_SORTS.status,
    },
    {
      id: 'actions',
      header: 'Actions',
      card: 'action',
      cell: (item) => (
        <RowActions item={item} selected={item.lineId === selectedId} onOpen={onOpen} />
      ),
      nowrap: true,
    },
  ];
}

/**
 * SA4: everyone past their election deadline, all sectors, one row per person (APP-067). Each waits
 * here, with the days since the letter and every contact so far, until a Stock Admin chases them,
 * gives a new deadline or closes it as all cash; nothing is paid as cash automatically.
 */
export function PastDeadlinePage(): ReactElement {
  const { data } = useSuspenseQuery(pastDeadlineQuery);
  const [params, setParams] = useSearchParams();
  const asOf = data.asOf;
  const items = data.items;
  const selected = selectedItem(items, params.get('line'), params.get('award'));
  // APP-149: sorted and filtered over the whole list, then paged; the queue's views keep one
  // saved sort (B6), and a column this list lacks opens at its own order.
  const barRef = useRef<HTMLDivElement>(null);
  const filters = useMemo(() => pastDeadlineFilters(items), [items]);
  const sortColumns = useMemo(() => columns(null, () => undefined), []);
  const table = useListTable({
    table: 'queue',
    rows: items,
    columns: sortColumns,
    defaultSort: PAST_DEADLINE_DEFAULT_SORT,
    defaultWords: PAST_DEADLINE_DEFAULT_WORDS,
    filters,
    tieBreak: pastDeadlineTie,
    barRef,
  });
  // APP-135: the whole list is on the screen; 25 show at a time.
  const paging = usePaging('past_deadline');
  const { anchorRef } = paging;
  const paged = clientPage(table.rows, paging.page, paging.size);
  useClampedPage(paging, paged.page.number);
  const requested = actionFromParam(params.get('action'));
  const [focusRequest, setFocusRequest] = useState(0);
  const requestFocus = (): void => {
    setFocusRequest((n) => n + 1);
  };
  // The panel's choice goes in the address too, so a row's button for the same person always
  // switches it, and Back and a shared link keep it.
  const chooseAction = (next: ElectionAction): void => {
    if (selected === null) return;
    setParams(
      (now) => {
        const out = new URLSearchParams(now);
        out.delete('award');
        out.set('line', selected.lineId);
        out.set('action', next);
        return out;
      },
      { replace: true },
    );
  };
  const oldest = oldestLetter(items);
  const windowDays = useQuery(settingQuery('election.windowDays', 'PAA')).data?.value;
  const windowWords = typeof windowDays === 'number' ? `${String(windowDays)}-day ` : '';

  return (
    <>
      <Hero
        eyebrow="Elections · Stock Admin"
        title="Past-deadline elections"
        sub={pastDeadlineSentence(items)}
        stats={[
          {
            label: 'All sectors · past deadline',
            value: formatCount(items.length),
            meta:
              items.length === 0
                ? 'Nothing waiting on you'
                : 'Waiting on you · none paid automatically',
            ...(items.length > 0 && { tone: 'gold' as const }),
          },
          {
            label: 'All sectors · oldest letter',
            value: oldest === null ? 'None' : daysWords(oldest.days),
            meta:
              oldest === null
                ? 'No letter is past its deadline'
                : `${oldest.item.person.name} · letter dated ${formatShortDate(oldest.date)}`,
          },
        ]}
        titleTestId="past-deadline-title"
      />
      <ManageTabs current="queue" />
      <PageBody>
        <div className={o.stack}>
          <BackLink href={QUEUE_VIEW.queue.href}>Work queue</BackLink>
          <p className={s.banner}>
            <Icon name="info" size={18} />
            <span>
              <strong>Missed elections are never paid as cash automatically.</strong> They wait
              here, with the days since the letter, until you chase, extend or close them.
            </span>
          </p>
          <Card
            title="Past the deadline"
            helpAnchor="past-deadline-list"
            help={{
              shows: `Everyone whose ${windowWords}election window closed without a choice, across all sectors, earliest deadline first, with the days since their letter and every reminder so far.`,
              canDo:
                'Sort by any heading, or filter by status and sector. Pick Chase, Extend or Close as all cash on a row: the person opens below at that step, where the email, the new date or the summary comes first. The award number opens its record.',
            }}
            aside={<span className={o.muted}>Today · {formatWeekdayDate(asOf)}</span>}
          >
            {items.length > 0 && (
              <div ref={barRef}>
                <FilterBar label="Filter the past-deadline elections" {...table.bar} />
              </div>
            )}
            {/* Empty, the header row stays and the day-one message is its one row (SA4 board); a
                filter that leaves nobody says so, with the way back (APP-149). */}
            <section ref={anchorRef} tabIndex={-1}>
              <DataTable
                caption="Past the deadline"
                columns={columns(selected?.lineId ?? null, requestFocus)}
                rows={paged.rows}
                rowKey={(item) => item.lineId}
                minWidth={1120}
                cards
                empty={{
                  title: 'No one past the deadline',
                  body: 'Elections that close without a choice show here.',
                }}
                emptyState={
                  items.length > 0 ? (
                    <NoMatches what="elections" onClear={table.clearFromEmpty} />
                  ) : (
                    <EmptyState
                      variant="first-day"
                      art="shield"
                      size="sm"
                      headline="No one past the deadline"
                      body={`When a ${windowWords}election window closes without a choice, the person lands here with the days since the letter, ready to chase, extend or close.`}
                    />
                  )
                }
                {...(items.length > 0 && { sort: table.sort, onSortChange: table.setSort })}
              />
            </section>
            <Pager
              label="Past the deadline"
              page={paged.page}
              onPage={(n) => {
                paging.setPage(n);
              }}
              onSize={(size) => {
                paging.setSize(size, pageOffset(paged.page));
              }}
            />
          </Card>
          <TwoColumns
            sideLabel="Election settings"
            main={
              selected === null ? (
                <Card title="The election" help={panelHelp} accent="cyan">
                  <EmptyState
                    variant="first-day"
                    art="calendar"
                    headline="Nothing to chase"
                    body="Open a past-deadline election here to email the employee again, set a new deadline with a reason, or close it as all cash. Every step is logged on the award."
                    action={{ label: 'Back to the work queue', href: QUEUE_VIEW.queue.href }}
                  />
                </Card>
              ) : (
                <ElectionPanel
                  key={selected.lineId}
                  item={selected}
                  asOf={asOf}
                  requested={requested}
                  onAction={chooseAction}
                  focusRequest={focusRequest}
                />
              )
            }
            side={
              <div className={o.stack}>
                <ElectionSettingsCard />
                <WhatEachDoes />
              </div>
            }
          />
        </div>
      </PageBody>
    </>
  );
}
